/**
 * Status Bar (VS Code-style)
 *
 * Displays contextual status information at the bottom of the app.
 * Only shows items that are currently relevant:
 * - Version (always -- app version from package.json; plugin version when connected)
 * - UE connection status (always when logged in)
 * - DVC and save status (only when passed -- i.e., inside a module)
 *
 * Version sources (single source of truth each):
 * - App version: `app.gamedevbuddy.com/package.json` -> injected by vite define.
 * - Plugin version: `GDBPlugin/GameDevBuddy.uplugin` -> exposed via
 *   `Core.GetPluginVersion` bridge call (IPluginManager at runtime).
 */

import * as React from 'react'

import {
  Check,
  Circle,
  AlertCircle,
  Cloud,
  HardDrive,
  Wifi,
  WifiOff,
} from 'lucide-react'

import { cn } from '@/shared/utils/cn'
import { useBridge } from '@/core/bridge/useBridge'

interface StatusBarProps {
  className?: string
  /** Save status -- null means don't show (e.g., on dashboard) */
  saveStatus?: 'saved' | 'saving' | 'error' | null
}

// Build-time constant injected by vite.config.ts `define`.
const APP_VERSION: string =
  typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev'

export function StatusBar({
  className,
  saveStatus = null,
}: StatusBarProps) {
  const { isConnected, callFunction } = useBridge()

  const [pluginVersion, setPluginVersion] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!isConnected) {
      // Bridge disconnected -- clear any stale value. App version still renders.
      setPluginVersion(null)
      return
    }

    let cancelled = false
    void (async () => {
      try {
        const result = await callFunction('GDBCore.GetPluginVersion')
        if (cancelled) return
        // Bridge wraps UFUNCTION FString returns as { returnValue: string }
        // (same shape used by GenerationColumn for
        // CharacterCreator.GetActiveProvider). Fall back to bare string for
        // future-proofing.
        const version = typeof result === 'string'
          ? result
          : typeof (result as { returnValue?: unknown })?.returnValue === 'string'
            ? (result as { returnValue: string }).returnValue
            : ''
        if (version.length > 0) {
          setPluginVersion(version)
        }
      } catch {
        // Bridge disconnected or function missing -- fall back silently.
        if (!cancelled) setPluginVersion(null)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [isConnected, callFunction])

  const editionName = isConnected ? 'Plugin Edition' : 'Cloud Edition'

  return (
    <div
      className={cn(
        'flex h-6 items-center justify-between border-t border-ue-border-medium bg-ue-bg-darker px-2 text-xs text-ue-text-secondary',
        className
      )}
    >
      {/* Left: edition label (Plugin Edition / Cloud Edition). Plugin version
          is rendered separately next to the app version so both sources of
          truth are visible side-by-side (see issue #64). */}
      <div className="flex items-center gap-3">
        <StatusItem
          icon={isConnected ? HardDrive : Cloud}
          label={editionName}
          status="default"
          title={
            isConnected && !pluginVersion
              ? 'Plugin version unavailable — DLL may predate the GDBCore.GetPluginVersion bridge function. Rebuild the plugin and restart UE.'
              : undefined
          }
        />
        {saveStatus && (
          <StatusItem
            icon={
              saveStatus === 'saved'
                ? Check
                : saveStatus === 'saving'
                  ? Circle
                  : AlertCircle
            }
            label={
              saveStatus === 'saved'
                ? 'All changes saved'
                : saveStatus === 'saving'
                  ? 'Saving...'
                  : 'Save error'
            }
            status={
              saveStatus === 'saved'
                ? 'success'
                : saveStatus === 'saving'
                  ? 'default'
                  : 'error'
            }
          />
        )}
      </div>

      {/* Center: brand + both version sources of truth side-by-side.
          - App version from package.json (via vite define __APP_VERSION__)
          - Plugin version from .uplugin (via GDBCore.GetPluginVersion bridge) */}
      <span className="flex items-center gap-2 text-xs">
        <span>GameDevBuddy</span>
        <span
          className="inline-flex items-center rounded-full border border-ue-accent-primary/30 bg-ue-accent-primary/15 px-1.5 py-0.5 text-[10px] font-medium text-ue-accent-primary"
          data-testid="beta-badge"
        >
          Beta
        </span>
        <span
          className="text-ue-text-muted"
          data-testid="app-version"
        >{`App ${APP_VERSION}`}</span>
        {pluginVersion && (
          <span
            className="text-ue-text-muted"
            data-testid="plugin-version"
          >{`Plugin ${pluginVersion}`}</span>
        )}
      </span>

      {/* Right side */}
      <div className="flex items-center gap-3">
        <StatusItem
          icon={isConnected ? Wifi : WifiOff}
          label={isConnected ? 'UE Connected' : 'UE Disconnected'}
          status={isConnected ? 'success' : 'error'}
        />
      </div>
    </div>
  )
}

interface StatusItemProps {
  icon: React.ComponentType<{ className?: string }>
  label: string
  status?: 'default' | 'success' | 'warning' | 'error'
  /** Native title tooltip shown on hover. */
  title?: string
}

function StatusItem({
  icon: Icon,
  label,
  status = 'default',
  title,
}: StatusItemProps) {
  return (
    <div
      className={cn(
        'flex items-center gap-1',
        status === 'success' && 'text-green-400',
        status === 'warning' && 'text-yellow-400',
        status === 'error' && 'text-red-400'
      )}
      title={title}
    >
      <Icon className="h-3 w-3" />
      <span>{label}</span>
    </div>
  )
}
